import {
  CommentableEntity,
  RichTextDocument,
  StaticCellId,
  UPDATE_COMMENT,
  UserId,
} from "@hex/common";
import { rgba } from "polished";
import React, { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";

import { HexButton, HexTooltip } from "../../hex-components";
import type { CommentModelFragment } from "../../hex-version-multiplayer/handlers/commentClientHandlers.generated";
import { useToggleState } from "../../hooks/useToggleState";
import { CyData } from "../../util/cypress";
import { useHexVersionAOContext } from "../../util/hexVersionAOContext";
import { OpenIcon, ResolveIcon } from "../icons/CustomIcons";

import { AppCommentIndicator } from "./AppCommentIndicator";
import { ChildCommentActionsContainerDiv, Comment } from "./Comment";
import { CommentActions } from "./CommentActions";
import { CommentAuthor } from "./CommentAuthor";
import { CommentCellLabel } from "./CommentCellLabel";
import { CommentEditor } from "./CommentEditor";
import { CommentReactionButton } from "./CommentReactionButton";
import { CommentReactionList } from "./CommentReactionList";
import { CommentText } from "./CommentText";

const SubThreadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const ChildComments = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 12px;

  width: 100%;
  margin-top: 12px;
  padding-left: 15px;

  &::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;

    display: block;
    width: 1px;

    background-color: ${({ theme }) => theme.borderColor.MUTED};

    content: "";
  }
`;

const ThreadHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  margin-bottom: 1px;
`;

export const ThreadAdminActions = styled.div`
  display: flex;
  flex: none;
  gap: 3px;
  align-self: flex-start;
  padding-left: 5px;

  transition: opacity ${({ theme }) => theme.animation.duration}
    ${({ theme }) => theme.animation.easing};
`;

const ThreadAction = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin-top: 2px;
`;

const ThreadWrapper = styled.div<{
  $isActionsOpen: boolean;
  $highlightAppComment: boolean;
}>`
  position: relative;
  z-index: 1;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  width: 100%;
  padding: 12px 12px 8px;

  ${ThreadAdminActions}, ${ChildCommentActionsContainerDiv} {
    opacity: ${({ $isActionsOpen }) => ($isActionsOpen ? 1 : 0)};
  }

  ${({ $highlightAppComment }) =>
    $highlightAppComment &&
    css`
      border-radius: ${({ theme }) => theme.borderRadius};
      box-shadow: inset 0 0 0 1px
          ${({ theme }) => rgba(theme.userActionColor, 0.4)},
        inset 0 0 0 4px ${({ theme }) => rgba(theme.userActionColor, 0.05)};
    `}

  &:hover {
    ${ThreadAdminActions},
    ${ChildCommentActionsContainerDiv} {
      opacity: 1;
    }
  }
`;

export interface CommentStub {
  readonly commentId: string;
  readonly creatorId: UserId;
  readonly richText: RichTextDocument;
  readonly createdDate: Date;
  readonly editedTextDate: Date | null;
  readonly entityType: CommentableEntity;
  readonly entityId: string;
  readonly reactions: CommentModelFragment["reactions"];
}

export interface CommentThreadProps {
  readonly canComment: boolean;
  readonly currentUserId: UserId | undefined;
  readonly resolved: boolean;
  readonly mainComment: CommentStub;
  readonly childComments: readonly CommentStub[];
  readonly appComment: boolean;
  readonly isAppCommentInProject?: boolean;
  readonly showCellLabel?: boolean;
}

export const CommentThread: React.ComponentType<CommentThreadProps> =
  React.memo(function CommentThread({
    appComment,
    canComment,
    childComments,
    currentUserId,
    isAppCommentInProject = false,
    mainComment,
    resolved,
    showCellLabel = false,
  }) {
    const {
      commentId,
      createdDate,
      creatorId,
      editedTextDate,
      entityId,
      entityType,
      reactions,
      richText,
    } = mainComment;
    const [
      isEditingComment,
      ,
      { setFalse: setNotEditingComment, setTrue: setEditingComment },
    ] = useToggleState(false);

    const [
      isReplyCreatorOpen,
      ,
      { setFalse: closeReplyCreator, setTrue: openReplyCreator },
    ] = useToggleState(false);

    const [
      isActionsOpen,
      setIsActionsOpen,
      { setFalse: closeActions, setTrue: openActions },
    ] = useToggleState(false);

    const { dispatchAO } = useHexVersionAOContext();

    const toggleResolved = useCallback(
      () =>
        dispatchAO(
          UPDATE_COMMENT.create({
            commentId,
            key: "resolved",
            value: !resolved,
          }),
        ),
      [commentId, dispatchAO, resolved],
    );

    const childCommentComponents = useMemo(
      () =>
        childComments
          .slice()
          .sort((a, b) => a.createdDate.getTime() - b.createdDate.getTime())
          .map((child) => (
            <Comment
              key={child.commentId}
              appComment={appComment}
              comment={child}
              currentUserId={currentUserId}
              editable={canComment}
              setIsActionsOpen={setIsActionsOpen}
            />
          )),
      [childComments, appComment, currentUserId, canComment, setIsActionsOpen],
    );

    return (
      <ThreadWrapper
        $highlightAppComment={isAppCommentInProject}
        $isActionsOpen={isActionsOpen}
      >
        <ThreadHeader>
          <CommentAuthor
            createdDate={createdDate}
            creatorId={creatorId}
            editedTextDate={editedTextDate}
          />
          <ThreadAdminActions
            // eslint-disable-next-line react/jsx-no-bind
            onClick={(e) => e.stopPropagation()}
          >
            {currentUserId != null && (
              <CommentReactionButton
                commentId={commentId}
                reactions={reactions}
                onToggle={setIsActionsOpen}
              />
            )}
            <HexTooltip
              content={resolved ? "Open" : "Resolve"}
              placement="bottom"
            >
              <HexButton
                $extraSmall={true}
                icon={resolved ? <OpenIcon /> : <ResolveIcon />}
                minimal={true}
                onClick={toggleResolved}
              />
            </HexTooltip>
            <CommentActions
              commentEditable={creatorId === currentUserId && canComment}
              commentId={commentId}
              isAppCommentInProject={isAppCommentInProject}
              setEditing={setEditingComment}
              onMenuClosed={closeActions}
              onMenuOpened={openActions}
            />
          </ThreadAdminActions>
        </ThreadHeader>
        {showCellLabel && mainComment.entityType === "CELL" && (
          <CommentCellLabel
            staticCellId={mainComment.entityId as StaticCellId}
          />
        )}
        <SubThreadContainer data-cy={CyData.COMMENT_BODY}>
          {isEditingComment ? (
            <CommentEditor
              appComment={appComment}
              commentId={commentId}
              creatorId={creatorId}
              entityId={entityId}
              entityType={entityType}
              helperText="Save"
              initialRichText={richText}
              onComplete={setNotEditingComment}
            />
          ) : (
            <>
              <CommentText
                commentRichText={richText}
                css={`
                  margin-bottom: 0px;
                `}
              />
              {reactions.length > 0 && (
                <CommentReactionList
                  commentId={commentId}
                  reactions={reactions}
                />
              )}
            </>
          )}
          {childComments.length > 0 && (
            <ChildComments>{childCommentComponents}</ChildComments>
          )}
        </SubThreadContainer>
        {canComment && (
          <ThreadAction>
            {isReplyCreatorOpen && currentUserId ? (
              <CommentEditor
                appComment={appComment}
                creatorId={currentUserId}
                css={`
                  margin-top: 5px;
                  margin-bottom: 3px;
                `}
                entityId={commentId}
                entityType={CommentableEntity.COMMENT}
                helperText={isAppCommentInProject ? "Reply in app" : "Reply"}
                initialRichText={null}
                onComplete={closeReplyCreator}
              />
            ) : (
              <>
                {!resolved && (
                  <HexButton
                    $extraSmall={true}
                    css={`
                      margin-left: -8px;
                    `}
                    minimal={true}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={(e) => {
                      e.stopPropagation();
                      openReplyCreator();
                    }}
                  >
                    Reply
                  </HexButton>
                )}
                {isAppCommentInProject && (
                  <AppCommentIndicator
                    commentEntityId={mainComment.commentId}
                  />
                )}
              </>
            )}
          </ThreadAction>
        )}
      </ThreadWrapper>
    );
  });
